import { exists, get } from "../utils/Safe";
import { fail } from "../utils/Assert";
import { getAudioChannelModel } from "./AudioModels";
import AudioChannel from "./AudioChannel";

/**
 * Manager class that handles separating audio into different channels and playing audio.
 */
export default class AudioPlayer
{
    /**
     * Normal audio playback type.
     * @type {string}
     * @readonly
     */
    get NORMAL() { return "normal"; }
    /**
     * Random audio playback type. Picks a random sound from a list to play.
     * @type {string}
     * @readonly
     */
    get RANDOM() { return "random"; }
    /**
     * Sequence audio playback type. Plays a list of sounds in sequence.
     * @type {string}
     * @readonly
     */
    get SEQUENCE() { return "sequence"; }
    /**
     * Cycle audio playback type. Plays the next sound in the list.
     * @type {string}
     * @readonly
     */
    get CYCLE() { return "cycle"; }

    constructor()
    {
        /**
         * A map of AudioChannel objects.
         * @type {Object.<string, AudioChannel>}
         */
        this.channels = { };
        this.onSoundPlayed = new Phaser.Signal();
    }

    /**
     * Cleans up the AudioPlayer.
     */
    destroy()
    {
        let keys = Object.keys(this.channels);
        while (keys.length > 0)
        {
            let key = keys.pop();
            this.channels[key].destroy();
            delete this.channels[key];
        }
    }

    /**
     * Sets up the AudioPlayer with the channels defined in the map.
     * If a channel already exists, then the tracks from the provided map
     * are added to the existing channel in the AudioPlayer.
     * @param {Object.<string, *>} channelMap
     */
    setupChannels(channelMap)
    {
        // Begin setting up the AudioPlayer's channels with the provided channel map.
        for (let key in channelMap)
        {
            if (exists(this.channels[key]))
            {
                // Channel exists, to append TrackModels to the channel.
                this.channels[key].appendTracks(get([channelMap[key], "trackMap"]));
            }
            else
            {
                // Channel doesn't exist, so add it.
                this.channels[key] = new AudioChannel(getAudioChannelModel(channelMap[key]));
                this.channels[key].onSoundPlayed.add(this._onSoundPlayed, this);
            }
        }
    }

    /**
     * Cleans up the AudioPlayer by removing tracks from channels defined in the provided
     * channel map. Once tracks are removed from the channel if the channel no longer has
     * tracks in it then it is destroyed and removed from the AudioPlayer.
     * @param {Object.<string, *>} channelMap
     */
    cleanupChannels(channelMap)
    {
        for (let key in channelMap)
        {
            if (this.validateChannel(key))
            {
                // Remove the tracks from the channel defined in the config's track map.
                this.channels[key].removeTracks(get([channelMap[key], "trackMap"]));

                // If the channel no longer has tracks in it, then we can destroy it.
                if (!this.channels[key].hasTracks)
                {
                    this.channels[key].destroy();
                    delete this.channels[key];
                }
            }
        }
    }

    _onSoundPlayed(key)
    {
        this.onSoundPlayed.dispatch(key);
    }

    /**
     * Plays audio specified in the playback options. The provided playbackOptions object
     * is meant to be an unsafe dynamic object. This object can come straight from a config.
     * @param {Phaser.Game} game
     * @param {*} playbackOptions Example: { "playbackType": "normal", "trackKey": "backgroundMusic" }
     * @param {function} onComplete Function to be called once playback is complete.
     * @param {*} onCompleteCtx 'this' object of the onComplete callback.
     */
    play(game, playbackOptions, onComplete = null, onCompleteCtx = null)
    {
        // Playback options are dynamic and is unsafe. Validate them before attempting to use them.
        playbackOptions = this.validatePlaybackOptions(playbackOptions);

        switch (playbackOptions.playbackType)
        {
            // Plays an audio track normally. If more than one track key
            // is provided then sounds are all at the same time. The onComplete
            // callback will get called when the first track in the list plays.
            case this.NORMAL:
                return this.playNormal(game, playbackOptions, onComplete, onCompleteCtx);

            // Plays a random track from track list.
            case this.RANDOM:
                return this.playRandom(game, playbackOptions, onComplete, onCompleteCtx);

            // Plays the tracks in the list in sequence. Calls
            // onComplete after the sequence is finished.
            case this.SEQUENCE:
                return this.playSequence(game, playbackOptions, onComplete, onCompleteCtx);

            // Plays the next track in the list each time playCycle is called.
            // Once the end of the list is reached, the cycle starts over.
            case this.CYCLE:
                return this.playCycle(game, playbackOptions, onComplete, onCompleteCtx);

            // Unsupported playback type.
            default:
                fail(`[AudioPlayer] Unsupported playbackType ${playbackOptions.playbackType}`);
                break;
        }
        return false;
    }

    /**
     * Plays an audio track normally. If more than one track key
     * is provided then sounds are all at the same time. The onComplete
     * callback will get called when the first track in the list plays.
     * @param {Phaser.Game} game
     * @param {*} playbackOptions
     * @param {function} [onComplete=null]
     * @param {*} [onCompleteCtx=null]
     */
    playNormal(game, playbackOptions, onComplete = null, onCompleteCtx = null)
    {
        let keys = typeof(playbackOptions.trackKey) === "string" ? [playbackOptions.trackKey] : playbackOptions.trackKey;
        let success = false;
        let channel = null;
        let callback = null;
        let callbackCtx = null;

        for (let i = 0; i < keys.length; i++)
        {
            // Set callback info.
            callback = i === 0 ? onComplete : null;
            callbackCtx = i === 0 ? onCompleteCtx : null;

            let key = this.formatKey(keys[i]);

            // Get the channel the track belongs to.
            channel = this.getChannelOfTrack(key);
            if (channel === null)
            {
                // Skipping because we couldn't find the channel the track belongs to.
                continue;
            }

            // Play the sound and track the success.
            success = channel.playTrack(game, key, callback, callbackCtx) || success;
        }

        return success;
    }

    /**
     * Plays a random track from track list.
     * @param {Phaser.Game} game
     * @param {*} playbackOptions
     * @param {function} [onComplete=null]
     * @param {*} [onCompleteCtx=null]
     */
    playRandom(game, playbackOptions, onComplete = null, onCompleteCtx = null)
    {
        let key;

        // Debug random VO
        let index = game.net.getQueryString("VO");
        if((typeof(index) == "string" || typeof(index) == "number") && +index < playbackOptions.trackKey.length)
        {
            key = playbackOptions.trackKey[index];
        }
        else
        {
            // Pick a random track to play.
            key = game.rnd.pick(playbackOptions.trackKey);
        }

        key = this.formatKey(key);

        // Find the channel the track belongs to.
        let channel = this.getChannelOfTrack(key);
        if (channel !== null)
        {
            return channel.playTrack(game, key, onComplete, onCompleteCtx);
        }
        return false;
    }

    /**
     * Plays the tracks in the list in sequence. Calls
     * onComplete after the sequence is finished.
     * @param {Phaser.Game} game
     * @param {*} playbackOptions
     * @param {function} [onComplete=null]
     * @param {*} [onCompleteCtx=null]
     */
    playSequence(game, playbackOptions, onComplete = null, onCompleteCtx = null)
    {
        let current = 0;
        let channel = null;
        let key = "";

        function playNext()
        {
            if (current >= playbackOptions.trackKey.length)
            {
                if (onComplete !== null)
                {
                    onComplete.call(onCompleteCtx);
                }
            }
            else
            {
                key = playbackOptions.trackKey[current++];
                key = this.formatKey(key);
                channel = this.getChannelOfTrack(key);

                if (channel === null)
                {
                    // If we couldn't find the channel, then skip this track in the sequence.
                    return playNext.call(this);
                }

                return channel.playTrack(game, key, playNext, this);
            }
            return playbackOptions.trackKey.length > 0;
        }
        return playNext.call(this);
    }

    /**
     * Plays the next track in the list each time playCycle is called.
     * Once the end of the list is reached, the cycle starts over.
     * @param {Phaser.Game} game
     * @param {*} playbackOptions
     * @param {function} [onComplete=null]
     * @param {*} [onCompleteCtx=null]
     */
    playCycle(game, playbackOptions, onComplete = null, onCompleteCtx = null)
    {
        // Check if the options have a current counter. Add one if it doesn't.
        if (!exists(playbackOptions.current))
        {
            playbackOptions.current = -1;
        }

        // Convert trackKey string to array.
        // Produces intuitive looping behaviour when passing in a string for trackKey.
        if (typeof(playbackOptions.trackKey) == "string")
        {
            playbackOptions.trackKey = [playbackOptions.trackKey];
        }

        // Get the index of the current track and the channel it belongs to.
        playbackOptions.current = (playbackOptions.current + 1) % playbackOptions.trackKey.length;
        let key = playbackOptions.trackKey[playbackOptions.current];
        key = this.formatKey(key);
        let channel = this.getChannelOfTrack(key);

        // If the channel exists, then play the track.
        if (channel !== null)
        {
            return channel.playTrack(game, key, onComplete, onCompleteCtx);
        }
        // Failed to play the track.
        return false;
    }

    /**
     * Stop all Phaser.Sound objects associated with the provided track key.
     * @param {string} trackKey
     * @param {boolean} [allowCallback=false]
     */
    stop(trackKey, allowCallback = false)
    {
        let channel = this.getChannelOfTrack(trackKey);
        if (channel !== null)
        {
            channel.stopTrack(trackKey, allowCallback);
        }
    }

    /**
     * Stops all Phaser.Sound objects within the provided channel.
     * @param {string} key
     * @param {boolean} [allowCallback=false]
     */
    stopChannel(key, allowCallback = false)
    {
        if (this.validateChannel(key))
        {
            this.channels[key].stopAll(allowCallback);
        }
    }

    /**
     * Stop all Phaser.Sound objects within all channels.
     * @param {boolean} [allowCallback=false]
     */
    stopAllChannels(allowCallback = false)
    {
        for (let key in this.channels)
        {
            this.channels[key].stopAll(allowCallback);
        }
    }

    /**
     *
     * @param {string} key
     * @param {number} volume
     */
    setChannelVolume(key, volume)
    {
        if (this.validateChannel(key))
        {
            this.channels[key].setVolume(volume);
        }
    }

    /**
     * Sets channel volume to 0.
     * @param {string} key
     */
    muteChannel(key)
    {
        if (this.validateChannel(key))
        {
            this.channels[key].mute();
        }
    }

    /**
     * Restores channel volume to its unmuted value.
     * @param {string} key
     */
    unmuteChannel(key)
    {
        if (this.validateChannel(key))
        {
            this.channels[key].unmute();
        }
    }

    /**
     * Returns the channel that has the specified track.
     * @param {string} trackKey
     */
    getChannelOfTrack(trackKey)
    {
        for (let key in this.channels)
        {
            if (this.channels[key].hasTrack(trackKey))
            {
                return this.channels[key];
            }
        }
        console.warn(`[AudioPlayer] Cannot find channel for ${trackKey}.`);
        return null;
    }

    /**
     * This is meant to be used ONLY by the AudioPlayer to validate playback options.
     * @param {*} options - Example: { "playbackType": "normal", "trackKey": "backgroundMusic" }
     * @internal
     */
    validatePlaybackOptions(options)
    {
        // Validate options.trackKey.
        if (!exists(options.trackKey))
        {
            fail("[AudioPlayer] Provided playback options is missing trackKey property.");
        }
        if (!(typeof(options.trackKey) == "string" || Array.isArray(options.trackKey)))
        {
            fail("[AudioPlayer] Provided playback options trackKey property must be a string or an array.");
        }

        // Validate options.playbackType. Defaults to "normal".
        options.playbackType = options.playbackType || this.NORMAL;

        // Validation complete, return the options.
        return options;
    }

    validateChannel(key)
    {
        if (exists(this.channels[key]))
        {
            return true;
        }
        else
        {
            console.warn(`[AudioPlayer] Cannot find channel ${key}.`);
            return false;
        }
    }

    formatKey(key)
    {
        var newKey = key;
        if(newKey.lastIndexOf("_") == key.length - 1)
        {
            newKey += Phaser.Device.desktop ? "DT" : "MBL";
        }
        return newKey;
    }
}
